// Wysokość dźwięku z mikrofonu, do stroika. Czysta matematyka, bez Web Audio
// (to spina `ui/stroik.ts`), więc testuje się na syntetycznej sinusoidzie.
//
// `autokorelacja` to przeniesiony algorytm ACF2+ z cwilso/PitchDetect
// (`js/pitchdetect.js`, funkcja `autoCorrelate`, MIT, licencja w
// `LICENSE-PitchDetect.txt` obok). Zmiany wobec oryginału: typy, próg ciszy
// jako parametr i ochrona przed wyjściem poza tablicę przy szukaniu
// pierwszego minimum.

const NAZWY = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;

/** Struny gitary w stroju standardowym, od najgrubszej, jako numery MIDI. */
export const STRUNY_STANDARD = [
  { nazwa: 'E', midi: 40 }, { nazwa: 'A', midi: 45 }, { nazwa: 'D', midi: 50 },
  { nazwa: 'G', midi: 55 }, { nazwa: 'H', midi: 59 }, { nazwa: 'e', midi: 64 },
] as const;

/**
 * Częstotliwość podstawowa w Hz albo -1, gdy sygnał jest za cichy albo
 * nieokresowy. `bufor` to próbki z `AnalyserNode.getFloatTimeDomainData`.
 */
export function autokorelacja(bufor: Float32Array, sampleRate: number, progCiszy = 0.01): number {
  let rozmiar = bufor.length;
  let rms = 0;
  for (let i = 0; i < rozmiar; i++) rms += bufor[i]! * bufor[i]!;
  rms = Math.sqrt(rms / rozmiar);
  if (rms < progCiszy) return -1;

  // Przycięcie brzegów do pierwszego przejścia blisko zera: autokorelacja
  // okna zaczynającego się w środku okresu daje fałszywe maksimum.
  let r1 = 0;
  let r2 = rozmiar - 1;
  const prog = 0.2;
  for (let i = 0; i < rozmiar / 2; i++) if (Math.abs(bufor[i]!) < prog) { r1 = i; break; }
  for (let i = 1; i < rozmiar / 2; i++) if (Math.abs(bufor[rozmiar - i]!) < prog) { r2 = rozmiar - i; break; }

  const buf = bufor.slice(r1, r2);
  rozmiar = buf.length;

  const c = new Float64Array(rozmiar);
  for (let i = 0; i < rozmiar; i++) {
    let suma = 0;
    for (let j = 0; j < rozmiar - i; j++) suma += buf[j]! * buf[j + i]!;
    c[i] = suma;
  }

  let d = 0;
  while (d < rozmiar - 1 && c[d]! > c[d + 1]!) d++;
  let maks = -1;
  let pozycja = -1;
  for (let i = d; i < rozmiar; i++) {
    if (c[i]! > maks) { maks = c[i]!; pozycja = i; }
  }
  if (pozycja <= 0 || pozycja >= rozmiar - 1) return -1;

  // Interpolacja paraboliczna wierzchołka: dokładność poniżej jednej próbki.
  let t0 = pozycja;
  const x1 = c[t0 - 1]!;
  const x2 = c[t0]!;
  const x3 = c[t0 + 1]!;
  const a = (x1 + x3 - 2 * x2) / 2;
  const b = (x3 - x1) / 2;
  if (a) t0 -= b / (2 * a);
  return sampleRate / t0;
}

export interface Nuta {
  /** Nazwa w notacji polskiej (`H` zamiast `B`). */
  nazwa: string;
  oktawa: number;
  midi: number;
  /** Odchyłka od nuty w centach, -50..+50. Ujemna = za nisko. */
  centy: number;
}

/** Najbliższa nuta równomiernie temperowana dla `hz` przy A4 = `a4`. */
export function nutaZCzestotliwosci(hz: number, a4 = 440): Nuta {
  const wys = 69 + 12 * Math.log2(hz / a4);
  const midi = Math.round(wys);
  const centy = Math.round((wys - midi) * 100);
  const nazwa = NAZWY[((midi % 12) + 12) % 12]!.replace(/^B$/, 'H');
  return { nazwa, oktawa: Math.floor(midi / 12) - 1, midi, centy };
}

/** Struna standardowego stroju najbliższa `hz` i odchyłka od niej w centach
 * (może przekraczać ±50, gdy struna jest mocno rozstrojona). */
export function najblizszaStruna(hz: number, a4 = 440): { indeks: number; nazwa: string; centy: number } {
  const wys = 69 + 12 * Math.log2(hz / a4);
  let najlepsza = 0;
  for (let i = 1; i < STRUNY_STANDARD.length; i++) {
    if (Math.abs(wys - STRUNY_STANDARD[i]!.midi) < Math.abs(wys - STRUNY_STANDARD[najlepsza]!.midi)) najlepsza = i;
  }
  const struna = STRUNY_STANDARD[najlepsza]!;
  return { indeks: najlepsza, nazwa: struna.nazwa, centy: Math.round((wys - struna.midi) * 100) };
}
